import {ChangeEvent, FC, useCallback, useEffect, useRef, useState} from 'react';
import {Box, Typography, IconButton, Chip} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import './JobResultHeader.scss';
import ArrButton from '../../../../common/arr-button/ArrButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {createJobNameFieldSchema} from './jobResultHeaderSchema';
import {
  useDownloadAIReviewJobResults,
  useUpdateAIReviewJobName,
} from '../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ReactComponent as DownloadIcon} from '../../../../../assets/images/downloadSVG.svg';
import {ReactComponent as ClearIcon} from '../../../../../assets/images/icon-close.svg';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {
  IGetAIReviewJobDetailsResponse,
  IGetAIReviewJobResultResponse,
} from '../../../../../api/types/aiReview.type';
import JobResultMetadataRow from './JobResultMetadataRow';
import IssueCodingJobResultMetrics from './IssueCodingJobResultMetrics';
import RelevancyGroundTruthMetrics from './RelevancyGroundTruthMetrics';
import {DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB} from '../../../../../shared/types';

export type JobResultHeaderVariant = 'legacy' | 'issueCodingV2' | 'relevancyV2';

type JobResultHeaderProps = {
  jobDetails: IGetAIReviewJobDetailsResponse | null;
  variant?: JobResultHeaderVariant;
  metricsDetails?: IGetAIReviewJobResultResponse;
  onDocErrorPanelOpen?: () => void;
  onNavigateToDocList?: (
    docsType: DOC_TYPE_MAPPING_FOR_AI_REVIEW_JOB,
    totalDocs?: number
  ) => void;
  onRefetchJobDetails?: () => void;
};

const JobResultHeader: FC<JobResultHeaderProps> = ({
  jobDetails,
  variant = 'legacy',
  metricsDetails,
  onDocErrorPanelOpen,
  onNavigateToDocList,
  onRefetchJobDetails,
}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isErrorMessage, setIsErrorMessage] = useState<string>('');
  const [jobNameValue, setJobNameValue] = useState<string>('');
  const [originalJobNameValue, setOriginalJobNameValue] = useState<string>('');
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType?.Success);
  const parentReviewJobID = jobDetails?.parentReviewJobID;
  const isRelevancyV2 = variant === 'relevancyV2';
  const isV2Header = variant === 'issueCodingV2' || isRelevancyV2;
  const hasGroundTruthSelection = Boolean(jobDetails?.groundTruthTagID);

  const inputRef = useRef<HTMLInputElement>(null);
  const isCancellingEditRef = useRef(false);
  const jobNameFieldSchema = createJobNameFieldSchema();

  const {mutate: updateJobName} = useUpdateAIReviewJobName();
  const {mutateAsync: downloadJobResults, isPending: isDownloadPending} =
    useDownloadAIReviewJobResults();

  useEffect(() => {
    if (jobDetails) {
      setJobNameValue(jobDetails?.jobName ?? '');
      setOriginalJobNameValue(jobDetails?.jobName ?? '');
    }
  }, [jobDetails]);
  useEffect(() => {
    if (isEditing && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isEditing]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setJobNameValue(value);
    const result = jobNameFieldSchema.safeParse(value);
    const errorMessage = !result?.success ? result.error.issues[0].message : '';
    setIsErrorMessage(errorMessage);
  };

  const cancelNameEdit = () => {
    isCancellingEditRef.current = true;
    setJobNameValue(originalJobNameValue);
    setIsErrorMessage('');
    setIsEditing(false);
  };

  const onHandleSave = () => {
    if (isCancellingEditRef.current) {
      return;
    }

    if (isErrorMessage) return;

    const trimmedValue = jobNameValue.trim();

    if (!trimmedValue) {
      setJobNameValue(originalJobNameValue);
      setIsEditing(false);
      return;
    }

    if (trimmedValue !== originalJobNameValue && jobDetails) {
      updateJobName(
        {
          jobId: String(jobDetails.jobId),
          jobName: trimmedValue,
        },
        {
          onSuccess: () => {
            setOriginalJobNameValue(trimmedValue);
            setIsEditing(false);
          },
          onError: () => {
            setJobNameValue(originalJobNameValue);
          },
        }
      );
    } else {
      setIsEditing(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onHandleSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelNameEdit();
    }
  };

  const handleNameClear = () => {
    cancelNameEdit();
  };

  const getSupportedText = (): string => {
    if (isErrorMessage) return isErrorMessage;

    return t('ai.review.jobResult.header.reachedChar50', {
      count: jobNameValue.length,
    });
  };

  const handleDownload = useCallback(async () => {
    try {
      const jobId = jobDetails?.jobId;
      const jobType = jobDetails?.jobType;

      if (!jobId || !jobType) {
        console.error('jobId or jobType is missing');
        return;
      }

      await downloadJobResults({jobId, jobType});
      setToastMessage(t('ai.review.metrics.downloadReportSuccess'));
      setToastStatus(ToastType.Success);
      setShowToast(true);
    } catch (error) {
      console.error('Export failed', error);
      setToastMessage(t('ai.review.metrics.downloadReportFailure'));
      setToastStatus(ToastType.Error);
      setShowToast(true);
    }
  }, [t, jobDetails?.jobId, jobDetails?.jobType, downloadJobResults]);

  const renderJobNameEditor = () => (
    <Box
      className={`jobresult-header-jobname${
        isEditing ? ' jobresult-header-jobname--editing' : ''
      }`}
    >
      {isEditing ? (
        <Box className="jobresult-header-jobname-field">
          <ArrTextField
            inputRef={inputRef}
            className="jobname-input"
            dataTestId="jobname-input-field"
            size="small"
            value={jobNameValue}
            slotProps={{
              htmlInput: {
                maxLength: 50,
              },
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e)}
            onBlur={onHandleSave}
            state={isErrorMessage ? 'error' : 'enabled'}
            onKeyDown={onKeyDown}
            supportText={getSupportedText()}
            showErrorIcon={!!isErrorMessage}
          />
          {Boolean(jobNameValue) && (
            <IconButton
              className="clear-icon"
              data-testid="jobname-clear-icon"
              aria-label={t('common.cancel')}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={handleNameClear}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ) : (
        <>
          <Typography
            className="typo-text subhead1_size"
            tabIndex={0}
            aria-label={t('ai.review.jobResult.header.jobName')}
            data-testid="job-result-header-job-name-value"
          >
            {jobNameValue}
          </Typography>
          <IconButton
            className="icon-button"
            aria-label={t('ai.review.jobResult.header.editJobName', {
              jobName: jobNameValue,
            })}
            data-testid="edit-jobname"
            onClick={() => {
              isCancellingEditRef.current = false;
              setIsEditing(true);
            }}
          >
            <EditOutlinedIcon className="svg-edit-icon" fontSize="medium" />
          </IconButton>
          {!isV2Header && (
            <ArrTooltipWrapper
              trigger={
                <InfoIcon
                  className="info-icon-label"
                  data-testid="toggle-info-tooltip"
                />
              }
              title={t('ai.review.jobResult.header.jobNameTooltipText')}
            >
              {t('ai.review.jobResult.header.jobNameTooltipText')}
            </ArrTooltipWrapper>
          )}
          {parentReviewJobID && (
            <Chip
              label={t('ai.review.jobResult.header.cloneOfJobId', {
                parentReviewJobID,
              })}
              className="clone-job-chip"
            />
          )}
        </>
      )}
    </Box>
  );

  const renderDownloadButton = () => (
    <ArrButton
      variant="primary"
      size="medium"
      aria-label={t('ai.review.jobResult.header.downloadResultDoc')}
      startIcon={<DownloadIcon />}
      dataTestId="job-result-header-download-button"
      className="job-result-header-download-button"
      onClick={handleDownload}
      disabled={isDownloadPending || !jobDetails?.jobId || !jobDetails?.jobType}
      loading={isDownloadPending}
    >
      {t('ai.review.jobResult.header.download')}
    </ArrButton>
  );

  if (isV2Header) {
    return (
      <Box
        className={`jobresult-header jobresult-header--issue-coding-v2${
          isRelevancyV2 ? ' jobresult-header--relevancy-v2' : ''
        }`}
      >
        <Box className="jobresult-header-v2-top">
          <Box className="jobresult-header-v2-title-col">
            <Box className="jobresult-header-v2-toolbar">
              <ArrButton
                variant="text"
                size="small"
                aria-label={t('ai.review.jobResult.header.backToAIReview')}
                onClick={() => navigate('/AI/Review')}
                dataTestId="job-result-header-back-to-home-button"
                className="jobresult-header-v2-exit-button"
              >
                <NavigateBeforeIcon fontSize="small" className="button-arrow" />
                {t('ai.review.jobResult.header.exit')}
              </ArrButton>
            </Box>
            <Box className="jobresult-header-v2-title-row">
              {renderJobNameEditor()}
            </Box>
          </Box>
          <Box className="jobresult-header-v2-actions-col">
            {renderDownloadButton()}
          </Box>
        </Box>

        <Box className="jobresult-header-v2-meta">
          <JobResultMetadataRow
            jobDetails={jobDetails}
            variant="issueCoding"
            onRefetchJobDetails={onRefetchJobDetails}
          />
        </Box>

        <Box className="jobresult-header-v2-metrics">
          <IssueCodingJobResultMetrics
            metricsDetails={metricsDetails}
            variant={isRelevancyV2 ? 'relevancy' : 'issueCoding'}
            onDocErrorPanelOpen={onDocErrorPanelOpen}
            onNavigateToDocList={onNavigateToDocList}
          />
        </Box>

        {isRelevancyV2 && hasGroundTruthSelection && (
          <Box className="jobresult-header-v2-ground-truth">
            <RelevancyGroundTruthMetrics jobId={jobDetails?.jobId} />
          </Box>
        )}

        {showToast && (
          <ArrToastNotification
            message={toastMessage}
            onClose={() => {
              setToastMessage('');
              setShowToast(false);
            }}
            status={[toastStatus]}
          />
        )}
      </Box>
    );
  }

  return (
    <Box className="jobresult-header">
      <Box className="jobresult-header-back-button">
        <ArrButton
          variant="text"
          size="small"
          aria-label={t('ai.review.jobResult.header.backToAIReview')}
          onClick={() => navigate('/AI/Review')}
          dataTestId="job-result-header-back-to-home-button"
        >
          <NavigateBeforeIcon fontSize="small" className="button-arrow" />
          {t('ai.review.jobResult.header.backToHome')}
        </ArrButton>
        {renderJobNameEditor()}
      </Box>
      <Box>
        <ArrButton
          variant="primary"
          size="medium"
          aria-label={t('ai.review.jobResult.header.relevancyResultDoc')}
          dataTestId="job-result-header-document-button"
          className="job-result-header-relevancy-button"
        >
          {t('ai.review.jobResult.header.relevancyDocs')}
        </ArrButton>
        {renderDownloadButton()}
      </Box>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
    </Box>
  );
};

export default JobResultHeader;
